/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect, useRef } from 'react';
import css from './Gallery.module.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faPlusSquare } from '@fortawesome/free-solid-svg-icons';

import { withRouter } from 'react-router-dom';
import { notifyError, notifySuccess } from '../../../../components/HotToast/HotToastProvider';
import ImageCropModal from '../../../../forms/ProfileSettingsForm/ImageCropModal';
import CompleteProfileModal from '../../../elements/complete-profile-modal/CompleteProfileModal';
import { CreditCard, User, Phone, Image as ImageIcon, FileText } from 'react-feather';

// Maximum number of photos a coach can attach to a single ad/listing.
// Note: this is a client-side cap only. Sharetribe Flex also enforces a
// marketplace-level limit via the Sharetribe Console (Build → Listings →
// Listing image limits). If saves with multiple images still fail after
// this cap is in place, check the Console setting.
const MAX_IMAGES = 3;

// ──────────────────────────────────────────────────────────────
// Publish-time readiness check (Subscription Gating Spec §1, §5.2)
//
// Returns a decision object describing whether the coach is allowed to
// publish this listing right now, and if not, why. Two reasons can
// block publish:
//
//   • 'no_subscription' — tier 0 / no active subscription. Per spec §1,
//     clicking Publish without a sub should open the pricing page (not
//     a modal). After successful checkout, the listing auto-publishes
//     (auto-publish wiring is a v2; today the coach just returns and
//     clicks Publish again).
//
//   • 'incomplete_profile' — one or more of Stripe/display name/phone/
//     photo/bio is missing (see getMissingProfileItems below). Per spec
//     §5.2 the FE should call out specifically which fields are missing
//     so the coach can fix them — shown via CompleteProfileModal.
function getPublishReadiness(currentUser) {
  if (!currentUser || !currentUser.attributes) {
    return { ready: false, reason: 'unknown' };
  }
  const profile = currentUser.attributes.profile || {};
  const metadata = profile.metadata || {};
  const subscriptionDetails = metadata.subscriptionDetails || null;
  const tier =
    subscriptionDetails && typeof subscriptionDetails.subscription === 'number'
      ? subscriptionDetails.subscription
      : 0;
  if (tier < 1) {
    return { ready: false, reason: 'no_subscription' };
  }
  if (
    subscriptionDetails.expiryDate &&
    parseInt(subscriptionDetails.expiryDate) < new Date().getTime()
  ) {
    return { ready: false, reason: 'subscription_expired' };
  }

  const missingItems = getMissingProfileItems(currentUser);
  if (missingItems.length > 0) {
    return { ready: false, reason: 'incomplete_profile', missingItems };
  }

  return { ready: true, reason: null };
}

// ──────────────────────────────────────────────────────────────
// Profile-completeness checklist (Subscription Gating Spec §5.2).
//
// Moved here from DashboardListingWrapper's "Create Ad" click handler —
// these used to block entry into the wizard entirely. Now they're
// checked at Submit time instead, after the ad has already been saved
// as a draft, so the coach can draft freely and only hits this gate
// once they try to actually publish. Rendered via CompleteProfileModal.
function getMissingProfileItems(currentUser) {
  const profile = (currentUser && currentUser.attributes && currentUser.attributes.profile) || null;
  const items = [];

  if (!currentUser || currentUser.stripeAccount == null) {
    items.push({
      icon: CreditCard,
      title: 'Connect a Stripe account',
      detail: "You'll need a payout account connected to get paid by clients.",
      path: '/account/payments/withdrawal',
    });
  }

  if (!(profile && profile.displayName && profile.displayName.length > 0)) {
    items.push({
      icon: User,
      title: 'Add your display name',
      detail: 'Clients see this name on your public profile and listings.',
      path: '/profile-settings',
    });
  }

  if (
    !(
      profile &&
      profile.protectedData &&
      profile.protectedData.phoneNumber &&
      profile.protectedData.phoneNumber.length > 0
    )
  ) {
    items.push({
      icon: Phone,
      title: 'Add your phone number',
      detail: "We'll only use this for account and booking notifications.",
      path: '/profile-settings',
    });
  }

  if (!(currentUser && currentUser.profileImage && currentUser.profileImage.id)) {
    items.push({
      icon: ImageIcon,
      title: 'Add a profile image',
      detail: 'Listings with a photo get more client engagement.',
      path: '/profile-settings',
    });
  }

  if (!(profile && profile.bio && profile.bio.length > 0)) {
    items.push({
      icon: FileText,
      title: 'Add a bio',
      detail: 'Tell clients about your coaching style and experience.',
      path: '/profile-settings',
    });
  }

  return items;
}

// ──────────────────────────────────────────────────────────────
// Listing-completeness check (defense-in-depth for the Services tab).
//
// The wizard's tab navigation (DashboardAddListingWrapper.updateTabs)
// switches tabs purely off the `params.tab` URL segment, with no check
// that earlier tabs were actually completed. That means a coach can
// jump straight to /l/edit/<id>/edit/gallery — via a stale bookmark,
// browser back/forward, or a hand-edited URL — and reach Publish
// without ever setting a price or the other Services fields.
// Gallery.js is the last stop before onPublishListingDraft is called,
// so this is the one place that can reliably block that: it reads the
// listing entity itself (the source of truth) rather than UI state,
// so it can't be bypassed by skipping a tab.
export function getListingCompleteness(listing) {
  const attributes = (listing && listing.attributes) || {};
  const publicData = attributes.publicData || {};
  const missingFields = [];

  const hasPrice = !!(attributes.price && attributes.price.amount > 0);
  if (!hasPrice) {
    missingFields.push('a price');
  }
  if (!Array.isArray(publicData['type-of-service']) || publicData['type-of-service'].length < 1) {
    missingFields.push('a type of service');
  }
  if (!Array.isArray(publicData['meeting-type']) || publicData['meeting-type'].length < 1) {
    missingFields.push('a meeting type');
  }
  if (!Array.isArray(publicData['service-delivery-to']) || publicData['service-delivery-to'].length < 1) {
    missingFields.push('who you deliver the service to');
  }

  return { complete: missingFields.length === 0, missingFields };
}

function Gallery(props) {
  const {
    onPublishListingDraft,
    onImageUpload,
    onUpdateListing,
    images,
    listing,
    currentUser,
    focusField,
  } = props;
  const imagesWrapRef = useRef(null);
  const videosWrapRef = useRef(null);
  // Shared by the focusField deep-link effect below and by validateFields'
  // scroll-to-first-invalid-field behavior, so both stay in sync.
  const FIELD_REFS = {
    videos: videosWrapRef,
    images: imagesWrapRef,
  };
  const scrollToField = fieldKey => {
    const target = FIELD_REFS[fieldKey];
    if (target && target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  const [videos, setVideos] = useState(['']);
  // Start empty; useEffect below populates from props.images for existing
  // listings. The previous placeholder ({ id: '', src: '' }) caused an
  // empty image id to leak into the save payload, which Sharetribe rejects.
  const [imageFields, setImageFields] = useState([]);
  const [errors, setErrors] = useState([]);
  // Per-field error message, shown inline right under the offending
  // field (in addition to the summary list) — see validateFields.
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState('');

  // Publish-time profile-completeness gate — see onSubmit / getMissingProfileItems.
  const [profileGateOpen, setProfileGateOpen] = useState(false);
  const [profileGateItems, setProfileGateItems] = useState([]);

  const inputToState = (index, event) => {
    event.persist();
    let tempArray = videos.slice();
    tempArray[index] = event.target.value;
    setVideos(tempArray);
  };

  const addInput = () => {
    if (
      /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm.test(
        videos[videos.length - 1]
      )
    ) {
      let tempArray = videos.slice();
      tempArray[tempArray.length] = '';
      setVideos(tempArray);
    } else {
      notifyError('Add a valid video url to add another one');
    }
  };

  const removeInput = key => {
    if (videos.length > 1) {
      let tempArray = videos.slice();
      setVideos(tempArray.filter((x, index) => index !== key));
    } else {
      setVideos(['']);
    }
  };

  const removePic = key => {
    // Defensive: image id may be a UUID object (uploaded image) or a
    // plain string in edge cases. Compare against both.
    const tempArray = imageFields.filter(x => {
      const id = x && x.id;
      const idStr = id && id.uuid ? id.uuid : id;
      return idStr !== key;
    });
    setImageFields(tempArray);
  };

  // Shared by the file picker and drag-and-drop: validate, then open the
  // crop modal instead of uploading directly.
  const selectFile = file => {
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      notifyError('Please choose an image file.');
      return;
    }

    // Enforce the per-listing image cap. Block before opening the
    // crop modal so the coach gets immediate feedback.
    if (imageFields.length >= MAX_IMAGES) {
      notifyError(`You can upload up to ${MAX_IMAGES} photos per ad.`);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCropImageSrc(objectUrl);
    setCropFileName(file.name);
    setCropModalOpen(true);
  };

  const fileUpload = event => {
    selectFile(event.target.files[0]);
    // Reset the input so re-selecting the same file triggers onChange
    event.target.value = '';
  };

  const openFilePicker = () => {
    if (uploading || !fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleDragOver = event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = uploading ? 'none' : 'copy';
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = event => {
    // Ignore leave events fired when moving between child elements.
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragActive(false);
  };

  const handleDrop = event => {
    event.preventDefault();
    setDragActive(false);
    if (uploading) return;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      notifyError('Drop one photo at a time — you can crop each one before it uploads.');
    }
    selectFile(files[0]);
  };

  const handleCropCancel = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropModalOpen(false);
    setCropImageSrc(null);
    setCropFileName('');
  };

  const handleCropConfirm = croppedFile => {
    setCropModalOpen(false);
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);

    setUploading(true);
    onImageUpload({
      id: `${croppedFile.name}_${Date.now()}`,
      file: croppedFile,
    }).then(res => {
      let reader = new FileReader();
      reader.readAsDataURL(croppedFile);
      reader.addEventListener('load', thumb => {
        let tempArray = imageFields.slice();
        tempArray[tempArray.length] = {
          id: res.data.data.id,
          src: thumb.target.result,
        };
        setImageFields(tempArray);
        setUploading(false);
      });
    }).catch(() => {
      // Without this the dropzone stayed stuck on "Uploading…" forever.
      setUploading(false);
      notifyError('Uploading your photo failed. Please try again.');
    });
  };

  const getImageFromState = () => {
    let tempArray = [];
    for (let i = 0; i < imageFields.length; i++) {
      tempArray[i] = imageFields[i].id;
    }
    return tempArray;
  };

  const validateFields = () => {
    let validationErrors = [];
    let newFieldErrors = {};
    let isValid = true;
    let firstInvalidField = null;
    const markInvalid = (field, message) => {
      validationErrors.push(message);
      newFieldErrors[field] = message;
      if (!firstInvalidField) firstInvalidField = field;
      isValid = false;
    };

    let videosAreValid = true;
    let errorIn = [];

    for (let i = 0; i < videos.length; i++) {
      const isNotValid =
        !/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm.test(
          videos[i]
        ) && i !== 0;
      if (isNotValid) {
        if (videosAreValid) {
          videosAreValid = false;
        }
        errorIn[errorIn.length] = i + 1;
      }
    }

    if (!videosAreValid) {
      markInvalid('videos', `Invalid video url(s), Check video fields ${errorIn.join(', ')}`);
    }

    if (imageFields.length < 1) {
      markInvalid('images', 'Add at least 1 photo');
    }

    setErrors(validationErrors);
    setFieldErrors(newFieldErrors);

    // Jump straight to whichever required field the coach missed first.
    if (!isValid && firstInvalidField) {
      scrollToField(firstInvalidField);
    }

    return isValid;
  };

  // advance=true ("Publish"): validate, block on incomplete Services data,
  // save, then run the publish-time gate (subscription + profile) before
  // actually publishing — this is the original onSubmit behavior.
  // advance=false ("Save draft"): same field validation and save, but
  // skips the Services-completeness check (that's a publish-only concern
  // — saving gallery images/videos doesn't require price/type-of-service
  // to exist yet) and never attempts to publish or navigate away.
  const onSubmit = advance => {
    let tempObj = { publicData: {} };
    if (imageFields.length > 0) {
      tempObj.images = getImageFromState();
    }

    if (videos.length > 0) {
      tempObj.publicData.videos = videos;
    }

    if (!validateFields()) {
      notifyError('Has non valid inputs');
      return;
    }

    if (advance) {
      // Block publish if the Services tab (price, type of service, meeting
      // type, service delivery) was never completed on this listing —
      // whether that's because the coach reached Gallery directly out of
      // order, or any other path. Checked against the listing entity
      // itself, not wizard/tab state, so it can't be bypassed.
      const listingCompleteness = getListingCompleteness(listing);
      if (!listingCompleteness.complete) {
        const missing = listingCompleteness.missingFields.join(', ');
        notifyError(`Complete your listing before publishing: please add ${missing}.`);
        props.history.push(`/l/edit/${listing.id.uuid}/edit/service`);
        return;
      }
    }

    // requestUpdateListing in the duck swallows API errors (it catches
    // them and dispatches updateListingError), which means the resolved
    // value here can be either a success response or the error action.
    // Inspect the result and only proceed to publish if the update
    // actually succeeded — otherwise surface the error to the coach.
    onUpdateListing('gallery', { ...tempObj, id: listing.id })
      .then(res => {
        const isErrorAction =
          res && res.type && String(res.type).toLowerCase().includes('error');
        if (isErrorAction || !res) {
          const apiMsg =
            res && res.payload && res.payload.message
              ? res.payload.message
              : 'Saving your photos failed. Please try again.';
          notifyError(apiMsg);
          return;
        }

        if (!advance) {
          notifySuccess('Draft saved');
          return;
        }

        // ── Publish-time gate ────────────────────────────────
        // The ad is now saved as a draft with everything the coach just
        // entered — nothing gets lost past this point. Only actually
        // publishing is gated on subscription + profile completeness.
        //
        // Per Subscription Gating Spec §1: no active subscription must
        // NOT open a modal — send the coach straight to the pricing
        // page (the draft is already saved; they come back and click
        // Publish again after checkout).
        //
        // Per §5.2: missing profile fields (Stripe, display name,
        // phone, photo, bio) block publish and are shown via
        // CompleteProfileModal so the coach can fix them without
        // losing the draft they just finished.
        const readiness = getPublishReadiness(currentUser);
        if (!readiness.ready) {
          if (readiness.reason === 'no_subscription') {
            notifyError('Subscribe to publish your listing — your draft is saved.');
            props.history.push('/account/payments/pricing');
            return;
          }
          if (readiness.reason === 'subscription_expired') {
            notifyError('Renew your subscription to publish — your draft is saved.');
            props.history.push('/account/payments/pricing');
            return;
          }
          if (readiness.reason === 'incomplete_profile') {
            setProfileGateItems(readiness.missingItems);
            setProfileGateOpen(true);
            return;
          }
          // 'unknown': let the publish go through — the post-create
          // /set-premium sync and any future sweep job will reconcile.
        }

        // Publish if not already
        onPublishListingDraft(listing.id)
          .then(() => {
            props.history.push(`/listings`);
            notifySuccess('Saved');
          })
          .catch(err => {
            const msg =
              err && err.message
                ? err.message
                : 'Could not publish your ad. Please try again.';
            notifyError(msg);
          });
      })
      .catch(err => {
        const msg =
          err && err.message
            ? err.message
            : 'Saving your photos failed. Please try again.';
        notifyError(msg);
      });
  };

  useEffect(() => {
    let tempArray = [];
    for (let i = 0; i < images.length; i++) {
      tempArray[i] = { id: images[i].id, src: images[i].attributes.variants['landscape-crop'].url };
    }
    setImageFields(tempArray);

    if (listing.attributes.publicData && listing.attributes.publicData.videos) {
      setVideos(listing.attributes.publicData.videos);
    }
  }, []);

  // Jump to and focus the photos field when arriving here because the
  // "Activate" check on a closed ad found it missing.
  useEffect(() => {
    if (!focusField) return;
    scrollToField(focusField);
  }, []);

  return (
    <div className={css.wrapper}>
      <div className={css.pageHeading}>
        <h2 className={css.pageTitle}>Gallery &amp; media</h2>
        <p className={css.pageSubtitle}>
          Add photos and video so clients can see who they'll be working with.
        </p>
      </div>

      <div ref={videosWrapRef} className={css.video}>
        <p className={css.label}>Add video URL</p>
        <p className={css.hint}>Paste a link to a YouTube or Vimeo video that introduces you or showcases your coaching style. Optional but highly recommended.</p>
        {videos.map((item, index) => {
          return (
            <div key={index}>
              <div className={css.inputContainer}>
                <input
                  className={`${css.input} ${fieldErrors.videos ? css.inputError : ''}`}
                  input="text"
                  value={item}
                  onChange={e => inputToState(index, e)}
                />
                <div className={css.remove} onClick={() => removeInput(index)}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
              {videos.length === index + 1 && (
                <div onClick={() => addInput()} className={css.addButton}>
                  <FontAwesomeIcon  icon={faPlusSquare} />
                  <div>Add URL</div>
                </div>
              )}
            </div>
          );
        })}
        {fieldErrors.videos && <p className={css.fieldError}>{fieldErrors.videos}</p>}
      </div>

      <div ref={imagesWrapRef} className={css.video}>
        <p className={css.label}>Add photos ({imageFields.length} of {MAX_IMAGES})</p>
        <p className={css.hint}>Upload up to {MAX_IMAGES} photos. Professional headshots or images of you coaching work best. You can crop each image after selecting it.</p>
        <div className={css.imageView}>
          {imageFields.map((item, index) => {
            const idStr = item && item.id && item.id.uuid ? item.id.uuid : item && item.id;
            return (
              <div key={idStr || index}>
                <img alt="listing" src={item.src} />
                <div className={css.removePic} onClick={() => removePic(idStr)}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
            );
          })}
          {imageFields.length < MAX_IMAGES ? (
            <div
              className={`${css.dropzone} ${dragActive ? css.dropzoneActive : ''}`}
              onDragEnter={handleDragOver}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="gallery-dropzone"
            >
              <input
                ref={fileInputRef}
                id="ListingImagePicker"
                type="file"
                accept="image/*"
                onChange={fileUpload}
              />
              {uploading ? (
                <p className={css.dropzoneText}>Uploading…</p>
              ) : (
                <>
                  <FontAwesomeIcon className={css.imagePickerIcon} icon={faPlus} />
                  <p className={css.dropzoneText}>
                    {dragActive ? 'Drop to upload' : 'Drag & drop a photo here'}
                  </p>
                  <span className={css.dropzoneOr}>or</span>
                  <button type="button" className={css.uploadButton} onClick={openFilePicker}>
                    Upload photo
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
        {fieldErrors.images && <p className={css.fieldError}>{fieldErrors.images}</p>}
      </div>
      <div className={css.errors}>
        {errors.map((item, index) => (
          <p key={index} className={css.error}>
            {item}
          </p>
        ))}
      </div>

      <div className={css.footerBar}>
        <span className={css.footerStep}>Step 3 of 3 — Gallery</span>
        <div className={css.footerActions}>
          <button
            type="button"
            className={css.saveDraftButton}
            onClick={() => onSubmit(false)}
          >
            Save draft
          </button>
          <button
            type="button"
            className={css.continueButton}
            onClick={() => onSubmit(true)}
          >
            Publish
          </button>
        </div>
      </div>

      <ImageCropModal
        isOpen={cropModalOpen}
        imageSrc={cropImageSrc}
        fileName={cropFileName}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        cropShape="rect"
        aspect={4 / 3}
        title="Adjust your ad image"
        subtitle="Zoom and drag to frame your image"
        saveLabel="Use image"
      />

      <CompleteProfileModal
        isOpen={profileGateOpen}
        onClose={() => setProfileGateOpen(false)}
        onNavigate={path => props.history.push(path)}
        items={profileGateItems}
      />
    </div>
  );
}

export default withRouter(Gallery);
